// Team voice state from /ws/radio text messages (docs/API.md section 9, open
// mic). Pure, so the rules are testable without a socket.

export interface Person {
  id: string;
  name: string;
}

export interface VoicePerson extends Person {
  muted: boolean;
}

export interface VoiceState {
  /** Everyone connected right now. */
  online: Person[];
  /** Who is in voice. */
  voice: VoicePerson[];
  you: { in_voice: boolean; muted: boolean };
}

export type ServerMessage = ({ type: "state" } & VoiceState) | { type: "pong" };

export const EMPTY: VoiceState = { online: [], voice: [], you: { in_voice: false, muted: false } };

function isPerson(v: unknown): v is Person {
  return !!v && typeof v === "object" && typeof (v as Person).id === "string" && typeof (v as Person).name === "string";
}

/**
 * Parse one text message. Anything else, including the retired radio
 * messages (talk_start, floor_denied, ...), is null and ignored.
 */
export function parseServerMessage(text: string): ServerMessage | null {
  let m: unknown;
  try {
    m = JSON.parse(text);
  } catch {
    return null;
  }
  if (!m || typeof m !== "object") return null;
  const o = m as Record<string, unknown>;
  if (o.type === "pong") return { type: "pong" };
  if (o.type !== "state") return null;
  const you = (o.you ?? {}) as Record<string, unknown>;
  return {
    type: "state",
    online: Array.isArray(o.online) ? o.online.filter(isPerson).map((p) => ({ id: p.id, name: p.name })) : [],
    voice: Array.isArray(o.voice)
      ? o.voice.filter(isPerson).map((p) => ({ id: p.id, name: p.name, muted: (p as { muted?: unknown }).muted === true }))
      : [],
    you: { in_voice: you.in_voice === true, muted: you.muted === true },
  };
}

/** `state` replaces everything; `pong` changes nothing. */
export function applyServerMessage(s: VoiceState, m: ServerMessage): VoiceState {
  if (m.type === "pong") return s;
  return { online: m.online, voice: m.voice, you: m.you };
}

/** The mono line above the orb while in voice: who is speaking, you first. "you, sam, ana". */
export function speakersLine(s: VoiceState, speaking: ReadonlySet<string>, meId: string): string {
  const names: string[] = [];
  if (speaking.has(meId)) names.push("you");
  for (const p of s.voice) if (p.id !== meId && speaking.has(p.id)) names.push(p.name.toLowerCase());
  return names.join(", ");
}

/** Before joining: "3 in voice" (or "no one in voice"). */
export function inVoiceLabel(s: VoiceState): string {
  const n = s.voice.length;
  return n === 0 ? "no one in voice" : `${n} in voice`;
}

export type VoiceMode = "out" | "joining" | "live" | "muted";

export function voiceMode(s: VoiceState, joining: boolean): VoiceMode {
  if (s.you.in_voice) return s.you.muted ? "muted" : "live";
  return joining ? "joining" : "out";
}
